import { captureException } from '@sentry/react';
import { minutesToMilliseconds } from 'date-fns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useEffectOnce } from 'react-use';

import {
  ActionResult,
  PejCapPrinterPlugin,
  Printer,
  PrinterStatusResult,
  SetPrinterOptions,
  SetPrinterResult,
} from '@frontend-libs/pej-cap-printer-plugin';

import { OrderModel, orderClient } from '@merchant-clients/order';

import { useToast } from '@frontend/components';
import { useClientQuery } from '@frontend/hooks';
import { tPrinter } from '@frontend/locales';

import { KeyValueStore } from '../storage';
import { mapOrderToPrinterOrder } from './map-order-to-printer-order';
import {
  PrinterAsyncAction,
  UsePrinterAsyncActionOptions,
  usePrinterAsyncAction,
} from './use-printer-async-action';

export type AutomaticPrintingType = 'DO_NOT_PRINT' | 'PRINT_AT_CONFIRMATION';

const automaticPrintingKey = 'automatic-printing-type';
const defaultAutomaticPrintingType: AutomaticPrintingType = 'DO_NOT_PRINT';

export interface PrinterContextType {
  printer: Printer | undefined;
  initializingPrinting: boolean;
  loadingPrinter: boolean;

  /**
   * Whether printing has been initialized and is ready to be used.
   */
  printingReady: boolean;

  refetch(): Promise<void>;
  setPrinterAction: PrinterAsyncAction<SetPrinterResult, SetPrinterOptions>;
  disconnectPrinterAction: PrinterAsyncAction<ActionResult>;
  automaticPrinting: AutomaticPrintingType;
  setAutomaticPrinting: (type: AutomaticPrintingType) => Promise<void>;
  getPrinterStatusAction: PrinterAsyncAction<PrinterStatusResult>;
  testPrintAction: PrinterAsyncAction<PrinterStatusResult>;
  printMerchantOrderAction: PrinterAsyncAction<ActionResult, OrderModel>;
}

export const PrinterContext = createContext<PrinterContextType>({
  printer: undefined,
  initializingPrinting: false,
  loadingPrinter: false,
  printingReady: false,
  refetch: async () => {
    // No-op outside PrinterProvider.
  },
  setPrinterAction: {
    isRunning: false,
    run: async () => ({ result: 'UNKNOWN_ERROR', message: '' }),
  },
  disconnectPrinterAction: {
    isRunning: false,
    run: async () => ({ result: 'SUCCESS' }),
  },
  automaticPrinting: defaultAutomaticPrintingType,
  setAutomaticPrinting: async () => {
    // No-op outside PrinterProvider.
  },
  getPrinterStatusAction: {
    isRunning: false,
    run: async () => ({ result: 'NO_PRINTER_STORED', message: '' }),
  },
  testPrintAction: {
    isRunning: false,
    run: async () => ({ result: 'NO_PRINTER_STORED', message: '' }),
  },
  printMerchantOrderAction: {
    isRunning: false,
    run: async () => ({ result: 'ERROR', message: '' }),
  },
});

export const PrinterProvider = (props: PropsWithChildren) => {
  const [initializingPrinting, setInitializingPrinting] = useState(true);
  const [automaticPrintingState, setAutomaticPrintingState] =
    useState<AutomaticPrintingType>(defaultAutomaticPrintingType);
  const { showToast } = useToast();

  const { data, isPending, refetch } = useClientQuery({
    queryKey: ['printer'],
    refetchInterval: minutesToMilliseconds(5),
    async onFetch() {
      return await PejCapPrinterPlugin.getPrinter();
    },
    onError: (error) => {
      captureException(error, {
        level: 'error',
        extra: {
          message: `Failed to resolve existing printers`,
        },
      });

      console.error(error);

      showToast({
        severity: 'error',
        body: tPrinter.errors.unknownError(),
      });
    },
  });

  const printer = data?.printer;

  const recoverFromSetPrinterError = useCallback(() => {
    console.log('Failed to set printer, disconnecting in-case it was stored');
    PejCapPrinterPlugin.disconnectPrinter().catch((error) => {
      captureException(error, {
        level: 'error',
        extra: {
          message: `Failed to disconnect printer during error recovery of set printer action`,
        },
      });
    });
  }, []);

  const setPrinterActionOptions = useMemo(
    (): UsePrinterAsyncActionOptions<SetPrinterResult, SetPrinterOptions> => ({
      name: 'set-printer',
      actionCallback: (options) => {
        return PejCapPrinterPlugin.setPrinter(options).then(async (result) => {
          if (result.result === 'SUCCESS') {
            await refetch();
          } else {
            recoverFromSetPrinterError();
          }

          return result;
        });
      },
      onError: (): Promise<SetPrinterResult> => {
        showToast({
          severity: 'error',
          body: tPrinter.errors.unknownError(),
        });

        recoverFromSetPrinterError();

        return Promise.resolve({
          result: 'UNKNOWN_ERROR',
          message: tPrinter.errors.unknownError(),
        });
      },
    }),
    [refetch, recoverFromSetPrinterError, showToast],
  );

  const setPrinterAction = usePrinterAsyncAction<
    SetPrinterResult,
    SetPrinterOptions
  >(setPrinterActionOptions);

  const disconnectPrinterActionOptions = useMemo(
    (): UsePrinterAsyncActionOptions<ActionResult> => ({
      name: 'disconnect-printer',
      actionCallback: () => {
        return PejCapPrinterPlugin.disconnectPrinter().then(async (result) => {
          if (result.result !== 'SUCCESS') {
            showToast({
              severity: 'error',
              body: tPrinter.errors.disconnectError(),
            });
          }

          // Even though we failed we try with a refetch anyways since the SDK
          // might have successfully removed the printer even though something
          // else failed.
          await refetch();
          return result;
        });
      },
      onError: (error) => {
        return Promise.resolve({
          result: 'ERROR',
          message: (error as Error).message,
        });
      },
    }),
    [refetch, showToast],
  );

  const disconnectPrinterAction = usePrinterAsyncAction<ActionResult>(
    disconnectPrinterActionOptions,
  );

  const setAutomaticPrinting = useCallback(
    async (type: AutomaticPrintingType) => {
      return KeyValueStore.set(automaticPrintingKey, type)
        .then(() => {
          setAutomaticPrintingState(type);
        })
        .catch((error) => {
          captureException(error, {
            level: 'error',
            extra: {
              message: `Failed to save automatic printing setting: ${type}`,
            },
          });

          console.error(error);
        });
    },
    [],
  );

  const getPrinterStatusActionOptions = useMemo(
    (): UsePrinterAsyncActionOptions<PrinterStatusResult> => ({
      name: 'get-printer-status',
      actionCallback: () => {
        return PejCapPrinterPlugin.getPrinterStatus().then((result) => {
          if (result.result !== 'SUCCESS') {
            throw new Error(
              result.message || 'Unknown error during get printer status',
            );
          }

          return result;
        });
      },
      onError: (error) => {
        return Promise.resolve({
          result: 'ERROR',
          message: (error as Error).toString(),
        });
      },
    }),
    [],
  );

  const getPrinterStatusAction = usePrinterAsyncAction<PrinterStatusResult>(
    getPrinterStatusActionOptions,
  );

  const testPrintActionOptions = useMemo(
    (): UsePrinterAsyncActionOptions<PrinterStatusResult> => ({
      name: 'test-print',
      actionCallback: () => {
        return PejCapPrinterPlugin.testPrint().then((result) => {
          if (result.result !== 'SUCCESS') {
            throw new Error(
              result.message || 'Unknown error during test print',
            );
          }

          return result;
        });
      },
      onError: (error) => {
        showToast({
          severity: 'error',
          body: tPrinter.errors.testPrintError(),
        });

        return Promise.resolve({
          result: 'ERROR',
          message: (error as Error).toString(),
        });
      },
    }),
    [showToast],
  );

  const testPrintAction = usePrinterAsyncAction<PrinterStatusResult>(
    testPrintActionOptions,
  );

  const printMerchantOrderActionOptions = useMemo(
    (): UsePrinterAsyncActionOptions<ActionResult, OrderModel> => ({
      name: 'print-merchant-order',
      actionCallback: (order) => {
        return orderClient.get(order.id).then((orderInfo) => {
          return PejCapPrinterPlugin.printMerchantOrderTicket({
            order: mapOrderToPrinterOrder(orderInfo),
          }).then(async (result) => {
            if (result.result === 'ERROR') {
              throw new Error(result.message || 'Unknown error during print');
            }

            return result;
          });
        });
      },
      onError: (error) => {
        showToast({
          severity: 'error',
          body: tPrinter.errors.printError(),
        });

        return Promise.resolve({
          result: 'ERROR',
          message: (error as Error).toString(),
        });
      },
    }),
    [showToast],
  );

  const printMerchantOrderAction = usePrinterAsyncAction<
    ActionResult,
    OrderModel
  >(printMerchantOrderActionOptions);

  useEffectOnce(() => {
    setInitializingPrinting(true);

    KeyValueStore.get(automaticPrintingKey)
      .then((type) => {
        if (type) {
          setAutomaticPrintingState(type as AutomaticPrintingType);
        }

        setInitializingPrinting(false);
      })
      .catch((error) => {
        captureException(error, {
          level: 'warning',
          extra: {
            message: `Failed to get automatic printing setting, defaulting to ${defaultAutomaticPrintingType}`,
          },
        });

        console.error(error);

        setAutomaticPrintingState(defaultAutomaticPrintingType);
        setInitializingPrinting(false);
      });
  });

  const value = useMemo(
    (): PrinterContextType => ({
      printer,
      initializingPrinting,
      loadingPrinter: isPending,
      printingReady: !!printer && !isPending && !initializingPrinting,
      refetch: async () => {
        await refetch();
      },
      setPrinterAction,
      disconnectPrinterAction,
      automaticPrinting: automaticPrintingState,
      setAutomaticPrinting,
      testPrintAction,
      getPrinterStatusAction,
      printMerchantOrderAction,
    }),
    [
      printer,
      isPending,
      initializingPrinting,
      refetch,
      setPrinterAction,
      disconnectPrinterAction,
      automaticPrintingState,
      setAutomaticPrinting,
      testPrintAction,
      getPrinterStatusAction,
      printMerchantOrderAction,
    ],
  );

  return (
    <PrinterContext.Provider value={value}>
      {props.children}
    </PrinterContext.Provider>
  );
};

export const usePrinterContext = () => {
  return useContext(PrinterContext);
};
